const OIL_TRAILER_SECTION_TYPE = 'Нефть';
const GALILEOSKY_DEVICE_TYPE = 1;
/** Порог «от 15 минут» для жёлтого значка/алерта непустого нефтевоза (секунды). */
export const PARKING_ALERT_SECONDS = 900;
/** Порог «от 6 часов» — выше него жёлтый нефтевозный сигнал не показывается (секунды). */
export const PARKING_LONG_SECONDS = 21600;
/** Порог суммы ДУТ для непустого нефтевоза. */
export const NONEMPTY_DUT_THRESHOLD = 300;
const DUT_KEYS = ['dut1', 'dut2', 'dut3', 'dut4', 'dut5', 'dut6'];
const STORAGE_KEY = 'nonemptyOilParkingAlerts';
const PARKING_START_TOLERANCE_MS = 3 * 60 * 1000;
const STORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const STORAGE_TOUCH_MS = 60 * 1000;
export const NONEMPTY_OIL_PARKING_ALERT_TITLE = 'Стоянка непустого нефтевоза';
export const NONEMPTY_OIL_PARKING_ALERT_TEXT = 'Стоянка непустого нефтевоза более 15 минут';
export const NONEMPTY_OIL_PARKING_ALERT_HEADER_COLOR = '#ffc107';
function parseUtcMs(value) {
    if (value == null || value === '') {
        return NaN;
    }
    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/);
    if (!match) {
        const parsed = Date.parse(String(value));
        return Number.isFinite(parsed) ? parsed : NaN;
    }
    return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4]), Number(match[5]), Number(match[6]));
}
function parkingStartedAtMs(data) {
    const datetimeMs = parseUtcMs(data === null || data === void 0 ? void 0 : data.datetime);
    const parkingTime = Number(data === null || data === void 0 ? void 0 : data.parking_time);
    if (!Number.isFinite(datetimeMs) || !Number.isFinite(parkingTime) || parkingTime < 0) {
        return null;
    }
    return datetimeMs - parkingTime * 1000;
}
function browserStorage() {
    try {
        if (typeof localStorage === 'undefined') {
            return null;
        }
        return localStorage;
    }
    catch (_a) {
        return null;
    }
}
function loadRecords(storage) {
    var _a;
    if (!storage) {
        return {};
    }
    try {
        const parsed = JSON.parse((_a = storage.getItem(STORAGE_KEY)) !== null && _a !== void 0 ? _a : 'null');
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return {};
        }
        return parsed;
    }
    catch (_b) {
        return {};
    }
}
function saveRecords(storage, records) {
    if (!storage) {
        return;
    }
    try {
        storage.setItem(STORAGE_KEY, JSON.stringify(records));
    }
    catch (error) {
        console.error('Failed to save parking records', error);
    }
}
export function isOilTrailer(tank) {
    if (!Array.isArray(tank)) {
        return false;
    }
    return tank.some((section) => Array.isArray(section) && section[1] === OIL_TRAILER_SECTION_TYPE);
}
export function sumDutValues(data, dutDefault = -1) {
    if (!data) {
        return 0;
    }
    return DUT_KEYS.map((key) => data[key])
        .filter((value) => value != null && value !== '' && Number(value) !== Number(dutDefault))
        .reduce((sum, value) => sum + Number(value), 0);
}
export function meetsNonemptyOilParking(transport, data, dutDefault = -1) {
    if (!transport || !data || !isOilTrailer(transport.tank)) {
        return false;
    }
    if (Number(transport.device_type) !== GALILEOSKY_DEVICE_TYPE) {
        return false;
    }
    if (Number(data.is_parked) !== 1) {
        return false;
    }
    if (data.geofence_name !== 'NA') {
        return false;
    }
    if (Number(data.parking_overtime) !== 1) {
        return false;
    }
    const parkingTime = Number(data.parking_time);
    if (!(parkingTime > PARKING_ALERT_SECONDS) || parkingTime > PARKING_LONG_SECONDS) {
        return false;
    }
    return sumDutValues(data, dutDefault) > NONEMPTY_DUT_THRESHOLD;
}
export function formatNonemptyOilParkingAlertText({ number, datetimeLabel }) {
    const parts = [NONEMPTY_OIL_PARKING_ALERT_TEXT + '.'];
    if (number) {
        parts.push(`«${number}»`);
    }
    if (datetimeLabel) {
        parts.push(datetimeLabel);
    }
    return parts.join(' ');
}
export function createNonemptyOilParkingAlertTracker({ dutDefault = -1, storage } = {}) {
    const sessions = new Map();
    const persistent = storage === undefined ? browserStorage() : storage;
    const records = loadRecords(persistent);
    const persist = () => saveRecords(persistent, records);
    const forget = (id) => {
        const key = String(id);
        if (!records[key]) {
            return;
        }
        delete records[key];
        persist();
    };
    const remember = (id, data, parkingTime) => {
        const key = String(id);
        const startedAt = parkingStartedAtMs(data);
        const previous = records[key];
        const now = Date.now();
        if (previous && previous.startedAt === startedAt && Number(previous.parkingTime) === parkingTime && now - Number(previous.lastSeen) < STORAGE_TOUCH_MS) {
            return;
        }
        Object.keys(records).forEach((recordId) => {
            const record = records[recordId];
            if (!record || now - Number(record.lastSeen) > STORAGE_TTL_MS) {
                delete records[recordId];
            }
        });
        records[key] = { startedAt, parkingTime, lastSeen: now };
        persist();
    };
    const sameStoredParking = (id, data, parkingTime) => {
        const record = records[String(id)];
        if (!record) {
            return false;
        }
        if (Date.now() - Number(record.lastSeen) > STORAGE_TTL_MS) {
            forget(id);
            return false;
        }
        const startedAt = parkingStartedAtMs(data);
        if (record.startedAt != null && startedAt != null) {
            return Math.abs(record.startedAt - startedAt) <= PARKING_START_TOLERANCE_MS;
        }
        return parkingTime >= Number(record.parkingTime);
    };
    return {
        reset() {
            sessions.clear();
        },
        evaluate(transport, data, previousData) {
            var _a;
            const id = (_a = transport === null || transport === void 0 ? void 0 : transport.id) !== null && _a !== void 0 ? _a : data === null || data === void 0 ? void 0 : data.transport_id;
            if (id == null || !data || data.is_parked == null) {
                return false;
            }
            const parked = Number(data.is_parked) === 1;
            const parkingTime = Number(data.parking_time);
            if (!parked) {
                sessions.delete(id);
                forget(id);
                return false;
            }
            if (!Number.isFinite(parkingTime) || parkingTime < 0) {
                return false;
            }
            let session = sessions.get(id);
            if (session && parkingTime < session.parkingTime) {
                sessions.delete(id);
                session = undefined;
                forget(id);
            }
            if (sameStoredParking(id, data, parkingTime)) {
                sessions.set(id, { parkingTime, alerted: true });
                remember(id, data, parkingTime);
                return false;
            }
            if (session === null || session === void 0 ? void 0 : session.alerted) {
                session.parkingTime = parkingTime;
                return false;
            }
            if (!meetsNonemptyOilParking(transport, data, dutDefault)) {
                sessions.set(id, { parkingTime, alerted: false });
                return false;
            }
            const previousParkingTime = Number(previousData === null || previousData === void 0 ? void 0 : previousData.parking_time);
            const previousSameParking = Number(previousData === null || previousData === void 0 ? void 0 : previousData.is_parked) === 1 && Number.isFinite(previousParkingTime) && previousParkingTime <= parkingTime;
            const previousMeets = previousSameParking && meetsNonemptyOilParking(transport, previousData, dutDefault);
            const crossedDuringWatch = Boolean(session) || (previousSameParking && !previousMeets);
            sessions.set(id, { parkingTime, alerted: true });
            remember(id, data, parkingTime);
            return crossedDuringWatch && !previousMeets;
        },
    };
}
